// Pure view helpers for the Squad module — ported from js/renderers-squads.js.
// DOM-free so the format + default-selection logic is unit-tested.

import type { CompetitionId, LeagueChoice } from "../data/league.js";

const POSITION_ABBREV: Record<string, string> = {
  goalkeeper: "GK",
  centre_back: "CB",
  full_back: "FB",
  midfielder: "MF",
  attacking_midfielder: "AM",
  striker: "ST",
};

/** Position template → its two-letter abbreviation, or "—" (renderers-squads.js:2). */
export function positionAbbrev(template: string | null): string {
  return (template && POSITION_ABBREV[template]) || "—";
}

/** Age in whole years from a "YYYY-MM-DD" dob. `now` is injectable for testing —
 *  the renderer uses the current date (renderers-squads.js:6). Null dob → null. */
export function ageFromDob(dob: string | null, now: Date = new Date()): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/** HOPS rating → 2dp, or "—" (renderers-squads.js:202). */
export function formatHops(hops: { rating: number } | null): string {
  return hops && typeof hops.rating === "number" ? hops.rating.toFixed(2) : "—";
}

/** The competition a Squad opens on: the one it had (row-click/undo restore) if
 *  captured, else the sticky player's competition, else the first
 *  (renderers-squads.js:99). */
export function resolveSquadCompetition(
  previous: CompetitionId | null | undefined,
  competitions: LeagueChoice[],
  stickyCompetition: CompetitionId | null | undefined,
): CompetitionId | null {
  const has = (id: CompetitionId | null | undefined) => id != null && competitions.some((c) => c.id === id);
  if (has(previous)) return previous as CompetitionId;
  if (has(stickyCompetition)) return stickyCompetition as CompetitionId;
  return competitions[0]?.id ?? null;
}

/** The team a Squad opens on: the one it had if still in this competition, else the
 *  sticky player's team (only when their competition matches), else the first
 *  (renderers-squads.js:107). */
export function resolveSquadTeam(
  previous: string | null | undefined,
  teams: string[],
  competition: CompetitionId | null,
  stickyCompetition: CompetitionId | null | undefined,
  stickyTeam: string | null | undefined,
): string | null {
  if (previous && teams.includes(previous)) return previous;
  if (stickyCompetition === competition && stickyTeam && teams.includes(stickyTeam)) return stickyTeam;
  return teams[0] ?? null;
}
